import * as Listener from './listener';
import * as Renderer from './renderer';
import * as ClueRenderer from './clue_renderer';
import { find, toNumber, isChildOf, clickInElement, clientHasLocalStorage,
         Direction, Position, Index } from './utils';
import { CrosswordType, CrosswordState, ClueModel, CrosswordModel,
         CrosswordRecordingEvent, CrosswordRecording } from './models';

type Clue = ClueModel | null;
type Square = Index.t | null;

export interface Changes extends Array<Change> {}

export interface Change {
  index: Index.t;
  position: Position;
  value: string;
}

interface EventHandler {
  element: HTMLElement;
  action: string;
  handler: (event?: Event) => void;
}

export class CrosswordController {
  private _root: HTMLElement;
  private _view: HTMLElement;
  private _listener: HTMLInputElement;

  private _model: CrosswordModel;
  private _crossword: string[];
  private _width: number;

  private _square: Square;
  private _direction: Direction;

  private _recording: CrosswordRecording|null = null;

  private _protectedElements: HTMLElement[];

  constructor(root: HTMLElement, model: CrosswordModel) {
    this._root = root;

    this._model = model;
    this._crossword = model.layout;
    this._width = model.size.cols;

    this._square = null;
    this._direction = Direction.Vertical;

    this._protectedElements = [];
  }

  public getCrosswordState(): CrosswordState {
    return this._model.getProgress(this._crossword);
  }

  public loadCrosswordState(state: string): void {
    let changes: Changes = [];

    state.split('').forEach((letter: string, i: number) => {
      if (this._model.isValidLetter(letter)) {
        let change: Change|null = this.change(Index.create(i, this._width), letter);
        if (change)
          changes.push(change);
      }
    });

    this.update(null, changes);
  }

  public change(square: Index.t, letter: string): Change|null {
    if (this._crossword[square.value] === letter)
      return null;

    return {
      'index': square,
      'position': Index.to2D(square),
      'value': letter
    };
  }

  // TODO: Poor structure and API
  public update(square: Square, changes?: Changes, clear?: boolean): void {
    if (square) {
      this._square = square;

      Listener.update(this._listener, Index.to2D(square), this._model.size);

      Renderer.highlight(this._view, Index.to2D(square),
                         this._grow(square, this._direction),
                         Direction.isReverse(this._direction));

      this._root.dispatchEvent(this._createSelectEvent(square));
    } else if (clear && square === null) {
      this._square = null;

      Renderer.highlight(this._view, null, []);

      this._root.dispatchEvent(this._createSelectEvent(square));
    }

    if (changes !== undefined && changes.length >= 1) {
      if (changes.length === 1) {
        let change: Change = changes[0];
        this._crossword[change.index.value] = change.value;

        for (let clue of this._model.getCluesByIndex(change.index)) {
          if (this._model.isSolutionForClue(clue.squares, this._crossword)) {
            Renderer.blink(this._view, clue.squares.map(Index.to2D));

            if (this._recording !== null && ( ! this._recording.cluesSolved.has(clue.id))) {
                this._recording.cluesSolved.add(clue.id);
                this._recording.events.push({
                  'clueId': clue.id,
                  'timestamp': Date.now()
                });
                this._storeRecording();
            }
          }
        }
      } else {
        for (let change of changes) {
          this._crossword[change.index.value] = change.value;
        }
      }

      Renderer.update(this._view, changes);

      this._root.dispatchEvent(this._createUpdateEvent());
    }
  }

  public render(): void {
    let list: HTMLElement;

    this._view = Renderer.render(this._root, this._model.type, this._model.size,
                                 this._model.getSquareList());
    this._listener = Listener.create(this._view);

    if (this._model.type === CrosswordType.Regular) {
      list = ClueRenderer.render(this._root, this._model.getClueList());
      this._bindEventHandlers([{
        'element': list,
        'action': 'click',
        'handler': this._onClueListClick
      }]);
      this._protectedElements.push(list);
    } else {
      this._bindEventHandlers();
    }

    this._protectedElements.push(this._view);
  }

  public clear(): void {
    let changes: Changes = [];

    this._crossword.forEach((square: string, i: number) => {
      if (square !== '.') {
        let change: Change|null = this.change(Index.create(i, this._width), '_');
        if (change)
          changes.push(change);
      }
    });
    this._crossword = this._model.layout;

    this.update(null, changes, true);
  }

  /**
   * Highlight any fully entered word that does not match the solution.
   */
  public highlightErrors(duration: number = 250): void {
    let errors: Square[] = [];

    this._model.getErrors(this._crossword).forEach((clue) => {
      if (this._model.isAttemptedSolutionForClue(clue.squares, this._crossword)) {
        for (let square of clue.squares) {
          errors.push(square);
        }
      }
    });

    if (errors.length !== 0) {
      if (errors[0] !== null) {
        // Center the viewport at the first square in the first
        // incorrect clue. When highlighting errors by blinking, this
        // is necessary to prevent the blinking being displayed outside
        // the users viewport.
        Renderer.centerViewportAt(this._view, Index.to2D(<Index.t>errors[0]));
      }
      Renderer.blink(this._view, errors.map(Index.to2D), true, duration);
    }
  }

  public addProtectedElements(element: HTMLElement): void {
    this._protectedElements.push(element);
  }

  public fillSquares(squares: HTMLElement[], letters: string[], spacing: number): void {
    let fill: Function = (square: Square, letter: string|undefined) => {
      if ( ! letter)
        return console.error('More squares to fill than available letters');
      if ( ! this._model.isValidLetter(letter.toUpperCase()))
        return console.error('Attempting to fill with invalid character(s)');

      this.update(square);
      this._handleInputAction(letter.toUpperCase());
    };

    squares.forEach((sq: HTMLElement, i: number) => {
      let square: Square = this._getElementPosition(sq);

      if (square === null || (! this._model.isSelectableSquare(square)))
        return console.error('Square to fill is not a valid square');

      if (spacing > 0)
        setTimeout(fill, i * spacing, square, letters[i]);
      else
        fill(square, letters[i]);
    });
  }

  public record(recordingId: string): void {
    // recordingId is a unique identifier for this user and crossword.
    if ( ! clientHasLocalStorage())
      return;

    let recording: CrosswordRecording|null = this.getRecording(recordingId);

    if (recording === null) {
      recording = {
        'id': recordingId,
        'cluesSolved': new Set<number>(),
        'events': []
      };
    }

    this._recording = recording;
    this._storeRecording();
  }

  public getRecording(recordingId: string): CrosswordRecording|null {
    if ( ! clientHasLocalStorage())
      return null;

    let recording: CrosswordRecording|undefined;
    let recordings: CrosswordRecording[];
    let data: string|null = localStorage.getItem('recordings');

    if (data === null)
      return null;

    recordings = JSON.parse(data);
    recording = recordings.find((r: CrosswordRecording) => r['id'] === recordingId);

    if (recording === undefined)
      return null;

    // Stored array needs to be converted to Set
    recording.cluesSolved = new Set<number>(recording.cluesSolved);

    return recording;
  }

  public setRecording(recordingId: string, storedRecording: CrosswordRecordingEvent[]): void {
    // Attempt to merge the given recording with any locally stored recording,
    // taking the local recording as the source of truth.
    if ( ! clientHasLocalStorage())
      return;

    let recording: CrosswordRecording;
    let localRecording: CrosswordRecording|null;

    localRecording = this.getRecording(recordingId);
    if (localRecording !== null) {
      recording = localRecording;
    } else {
      recording = {
        'id': recordingId,
        'cluesSolved': new Set<number>(),
        'events': []
      };
    }

    try {
      // copy recording data, allowing us to roll back any updates if merging fails.
      let mergedEvents: CrosswordRecordingEvent[] = recording.events.slice();
      let mergedCluesSolved: Set<number> = new Set<number>(recording.cluesSolved);

      storedRecording.forEach((event: CrosswordRecordingEvent) => {
        let hasEvent: number = recording.events.findIndex((e: CrosswordRecordingEvent) => {
          return e.clueId === event.clueId;
        });

        if (hasEvent < 0) { // no event exists for this clue
          mergedEvents.push({
            'clueId': event.clueId,
            'timestamp': event.timestamp
          });
        }

        mergedCluesSolved.add(event.clueId);
      });

      this._recording = recording;
      this._recording.events = mergedEvents;
      this._recording.cluesSolved = mergedCluesSolved;
    } catch (exn) {
      this._recording = recording;
    }

    this._storeRecording();
  }

  private async _storeRecording(): Promise<null> {
    if ( ! clientHasLocalStorage())
      return null;
    if (this._recording === null)
      return null;

    let stored: boolean = false;
    let recordings: CrosswordRecording[];
    let data: string|null = localStorage.getItem('recordings');

    if (data === null)
      data = '[]';

    recordings = JSON.parse(data);
    for (let i = 0; i < recordings.length; i++) {
      if (recordings[i]['id'] === this._recording['id']) {
        recordings[i]['events'] = this._recording['events'];
        // Set needs to be converted to array before storing
        recordings[i]['cluesSolved'] = <any>Array.from(this._recording['cluesSolved']);
        stored = true;
      }
    }

    if ( ! stored) {
      recordings.push({
        'id': this._recording['id'],
        'events': this._recording['events'],
        'cluesSolved': <any>Array.from(this._recording['cluesSolved']),
      });
    }

    localStorage.setItem('recordings', JSON.stringify(recordings));

    return null;
  }

  /**
   * Grows a selection of squares from an index.
   * If the given index and direction matches a clue,
   * all squares used by that clue are returned, otherwise
   * only the given index is returned as a Position.
   */
  private _grow(index: Index.t, dir: Direction): Position[] {
    let clue: Clue;

    clue = this._model.getClueByIndex(index, dir);
    if (clue) {
      return clue.squares.map((i: Index.t) => Index.to2D(i));
    } else {
      return [Index.to2D(index)];
    }
  }

  private _createSelectEvent(square: Square): CustomEvent {
    let clue: Clue;
    let index: Position | null = null;
    let clueId: number | null = null;

    if (square) {
      index = Index.to2D(square);
      clue = this._model.getClueByIndex(square, this._direction);
      if (clue) {
        clueId = clue.id;
      }
    }

    return new CustomEvent('wordcross:select', {
      detail: {
        'square': index,
        'clue': clueId
      },
      bubbles: false,
      cancelable: true
    });
  }

  private _createUpdateEvent(): CustomEvent {
    return new CustomEvent('wordcross:update', {
      detail: {
        isCompleted: this._model.isSolution(this._crossword)
      },
      bubbles: false,
      cancelable: true
    });
  }

  private _bindEventHandlers(handlers?: EventHandler[]): void {
    window.addEventListener('mousedown', this._onClickPreventBlur.bind(this), false);

    this._view.addEventListener('click', this._onClick.bind(this), false);

    this._listener.addEventListener('input', this._onInput.bind(this), false);
    this._listener.addEventListener('keydown', this._onKeyDown.bind(this), false);
    this._listener.addEventListener('blur', this._onListenerBlur.bind(this), false);

    if (handlers !== undefined) {
      handlers.forEach((e: EventHandler) => {
        e.element.addEventListener(e.action, e.handler.bind(this), false);
      });
    }
  }

  private _onClick(event: MouseEvent): boolean {
    let target: HTMLElement;
    let square: Square;

    target = <HTMLElement>event.target;
    square = this._getElementPosition(target);

    if (square) {
      if (event.ctrlKey && this._model.isSelectableSquare(square)) {
        this._onCtrlClick(square);
      } else if (event.shiftKey && this._model.isSelectableSquare(square)) {
        this._onShiftClick(square);
      } else {
        if (target.hasAttribute('data-id')) {
          this._onClueClick(target);
        } else if (target.parentElement &&
                   target.parentElement.hasAttribute('data-id')) {
          this._onClueClick(target.parentElement);
        } else if (this._model.isSelectableSquare(square)) {
          this._onSquareClick(square);
        }
      }
    }

    return false;
  }

  /**
   * Clears any letters within a clue matching the clicked index
   * that is not part of another clue. If no clue matches,
   * only the clicked square is cleared.
   */
  private _onCtrlClick(index: Index.t): void {
    let changes: Change[];
    let clue: Clue;

    clue = this._model.getClueByIndex(index, this._direction);
    if (clue) {
      changes = clue.squares.reduce((acc: Change[], square: Index.t) => {
        if (this._model.getCluesByIndex(square).length === 1) {
          let change: Change|null = this.change(square, '_');
          if (change)
            acc.push(change);
        }
        return acc;
      }, []);
    } else {
      changes = <Change[]>[this.change(index, '_')].filter(x => x);
    }

    this.update(index, changes);
  }

  /**
   * Clears all letters within a clue matching the clicked index.
   * If no clue matches, only the clicked square is cleared.
   */
  private _onShiftClick(index: Index.t): void {
    // TODO: Practically the same as _onCtrlClick. Can be merged
    //       under a _onModifierClick(index: Index.t, mod: string): void.
    let clue: Clue;
    let change: Change|null;
    let changes: Change[] = [];

    clue = this._model.getClueByIndex(index, this._direction);
    if (clue) {
      for (let square of clue.squares) {
        change = this.change(square, '_');
        if (change)
          changes.push(change);
      }
    } else {
      change = this.change(index, '_');
      if (change)
        changes.push(change);
    }

    this.update(index, changes);
  }

  private _onClueClick(element: HTMLElement): void {
    let clue: Clue;
    clue = this._model.getClueById(toNumber(element.getAttribute('data-id')));
    if (clue) {
      this._direction = clue.direction;
      this.update(clue.origin);
    }
  }

  private _onSquareClick(index: Index.t): void {
    if (Direction.isReverse(this._direction)) {
      this._direction = Direction.reverse(this._direction);
    }

    if (this._shouldSwapDirection(index)) {
      this._swapDirection();
    }

    this.update(index);
  }

  private _onClueListClick(event: MouseEvent): void {
    let target: HTMLElement;

    target = <HTMLElement>event.target;
    if (target.hasAttribute('data-id')) {
      this._onClueClick(target);
    }
  }

  /**
   * Consumes click events inside the crossword view to prevent
   * the active element listener from losing and resetting focus
   * at inopportune moments. Has the perhaps unfortunate effect of
   * preventing text selection.
   */
  private _onClickPreventBlur(event: MouseEvent): boolean {
    for (let i: number = 0; i < this._protectedElements.length; i++) {
      if (isChildOf(<HTMLElement>event.target, this._protectedElements[i]) &&
          clickInElement(event, this._protectedElements[i])) {
        event.preventDefault();
        return false;
      }
    }
    return true;
  }

  private _onInput(event: KeyboardEvent): boolean {
    event.preventDefault();

    const letter: string = Listener.value(this._listener);
    if (this._model.isValidLetter(letter))
      this._handleInputAction(letter);

    return false;
  }

  private _onKeyDown(event: KeyboardEvent): void {
    // Do nothing if the event was already processed
    if (event.defaultPrevented) return;
    // No square to receive action
    if (this._square === null) return;

    switch (event.key) {
      // To support older browsers, some of the cases are duplicates
      case 'Enter':
        this._handleRotationAction(event.shiftKey);
        break;
      case 'Tab':
        this._handleWordJumpAction(this._direction, event.shiftKey);
        break;
      case 'Backspace':
        this._handleClearAction(Direction.reverse(this._direction));
        break;
      case ' ':
      case 'Spacebar':
        this._handleClearAction(this._direction);
        break;
      case 'Del':
      case 'Delete':
        this._handleClearAction(Direction.None);
        break;
      case 'Up':
      case 'ArrowUp':
        this._handleArrowAction(Direction.VerticalReverse);
        break;
      case 'Down':
      case 'ArrowDown':
        this._handleArrowAction(Direction.Vertical);
        break;
      case 'Left':
      case 'ArrowLeft':
        this._handleArrowAction(Direction.HorizontalReverse);
        break;
      case 'Right':
      case 'ArrowRight':
        this._handleArrowAction(Direction.Horizontal);
        break;
      default:
        return; // We do not handle the event, quit and pass it on.
    }

    event.preventDefault();
 }

  private _onListenerBlur(): void {
    this.update(null, [], true);
  }

  /**
   * Rotates the current direction.
   * A reverse is performed if the current direction does not
   * align with the reverse parameter. In this case the
   * direction is not swapped. If the current direction does
   * align with the reverse parameter, the direction is only
   * swapped.
   */
  private _handleRotationAction(reverse: boolean): void {
    if (reverse && !Direction.isReverse(this._direction)) {
      this._direction = Direction.reverse(this._direction);
    } else if (!reverse && Direction.isReverse(this._direction)) {
      this._direction = Direction.reverse(this._direction);
    } else {
      this._swapDirection();
    }

    this.update(this._square);
  }

  private _handleWordJumpAction(dir: Direction, reverse: boolean): void {
    let square: Index.t;
    let modifier: number;
    let clues: ClueModel[];
    let clue: Clue;
    let currentClue: Clue;

    modifier = reverse ? -1 : 1;

    // TODO: Verify that this is reasonable
    square = <Index.t>this._square;
    currentClue = this._model.getClueByIndex(square, dir);
    if (currentClue) {
      square = currentClue.origin;
    }

    clue = null;
    while (this._model.inBounds(square)) {
      clues = this._model.getCluesByIndex(square);
      clues = clues.filter(c => Index.equal(c.origin, square));

      if (clues.some((c: ClueModel) => c !== currentClue)) {
        if (clues.length === 1) { clue = clues[0]; };
        if (clues.length === 2) {
          if (clues.every(c => c !== currentClue)) {
            if (reverse) {
              clue = find(clues, c => Direction.equal(c.direction, Direction.Vertical));
            } else {
              clue = find(clues, c => Direction.equal(c.direction, Direction.Horizontal));
            }
          } else {
            if (reverse && Direction.equal(dir, Direction.Vertical)) {
              clue = find(clues, c => Direction.equal(c.direction, Direction.Horizontal));
            } else if (!reverse && Direction.equal(dir, Direction.Horizontal)) {
              clue = find(clues, c => Direction.equal(c.direction, Direction.Vertical));
            } else {
              square = Index.create(square.value + modifier, square.width);
              continue;
            }
          }
        }

        break;
      }

      square = Index.create(square.value + modifier, square.width);
    }

    if (clue) {
      this._direction = clue.direction;
      this.update(square);
    }
  }

  private _handleClearAction(dir: Direction): void {
    if (this._square) {
      let change: Change|null = this.change(this._square, '_');
      if (change) {
        this.update(this._getNextSquare(this._square, dir), [change]);
      } else {
        this.update(this._getNextSquare(this._square, dir));
      }
    }
  }

  private _handleArrowAction([row, col]: Direction): void {
    let index: Square;
    let square: Square;

    // No square to receive action
    if (this._square === null) { return; }

    index = this._square;
    square = this._getNextSquare(index, [row, col]);

    while (square === null) {
      if (index && this._model.inBounds(index)) {
        index = Index.next(index, row, col);
        if (index) {
          square = this._getNextSquare(index, [row, col]);
        } else { // No next square
          break;
        }
      } else { // No index or index out of bounds
        break;
      }
    }

    if (square) {
      if (this._shouldSwapDirection(square)) {
        this._swapDirection();
      }
      this.update(square);
    }
  }

  private _handleInputAction(letter: string): void {
    if (this._square) {
      let change: Change|null = this.change(this._square, letter);
      if (change) {
        this.update(this._getNextSquare(this._square, this._direction), [change]);
      } else {
        this.update(this._getNextSquare(this._square, this._direction));
      }
    }
  }

  private _getElementPosition(el: HTMLElement): Square {
    let row: number;
    let col: number;

    while (el !== this._view) {
      if (el.hasAttribute('data-row') && el.hasAttribute('data-col')) {
        row = parseInt(<string>el.getAttribute('data-row'), 10);
        col = parseInt(<string>el.getAttribute('data-col'), 10);
        return Index.from2D(row, col, this._width);
      }

      if (el.parentElement === null) { break; }
      el = el.parentElement;
    }

    return null;
  }

  private _getNextSquare(index: Index.t, [row, col]: Direction): Square {
    let next: Square;

    if (Direction.equal(Direction.None, [row, col])) {
      return index;
    }

    next = Index.next(index, row, col);
    if (next && this._model.isSelectableSquare(next)) {
      return next;
    } else {
      return null;
    }
  }

  /**
   * Returns TRUE under the following circumstances:
   *   1. Passed square is the same as the active square.
   *   2. Passed square has no valid neighbour, meaning it is locked.
   *      NOTE: This assumes swapping direction will unlock the square,
   *            which is not guaranteed.
   */
  private _shouldSwapDirection(index: Index.t): boolean {
    let neighbour: Function = this._getNextSquare.bind(this);
    return this._square !== null
        && Index.equal(index, this._square)
        || neighbour(index, Direction.reverse(this._direction)) === null
        && neighbour(index, this._direction) === null;
  }

  private _swapDirection(): void {
    this._direction = Direction.swap(this._direction);
  }
}
